import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useCategoryStore } from '../stores/categoryStore';
import { t } from '../stores/languageStore';
import { postApi, siteConfigApi, adApi } from '../utils/api';
import { roomApi } from '../utils/roomApi';
import { Post, Ad, Room } from '../types';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Settings, 
  Search,
  Home,
  LayoutDashboard,
  ChevronDown,
  MessageSquare,
  Plus
} from 'lucide-react';
import AdDisplay from './AdDisplay';
import NotificationDropdown from './NotificationDropdown';
import NotificationBanner from './NotificationBanner';

const injectedGlobalAds = new Set<number>();

const Layout: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuthStore();
  const { categories, fetchCategories } = useCategoryStore();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedCategories, setExpandedCategories] = React.useState<number[]>([]);
  const [hotPosts, setHotPosts] = useState<Post[]>([]);
  const [hotPostsLoading, setHotPostsLoading] = useState(true);
  const [copyrightText, setCopyrightText] = useState('© 2024 桃花坛. All rights reserved.');
  const [siteName, setSiteName] = useState('桃花坛');
  const [globalAds, setGlobalAds] = useState<Ad[]>([]);
  const [headerAds, setHeaderAds] = useState<Ad[]>([]);
  const [adsLoading, setAdsLoading] = useState(true);
  const [userRooms, setUserRooms] = useState<Room[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    fetchCategories();
    fetchCopyright();
    fetchSiteName();
    fetchGlobalAds();
    if (isAuthenticated) {
      fetchUserRooms();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchHotPosts = async () => {
      try {
        const response = await postApi.getHotPosts(10);
        if (response.success && response.data) {
          setHotPosts(response.data);
        }
      } catch (error) {
        console.error('获取热门帖子失败:', error);
      } finally {
        setHotPostsLoading(false);
      }
    };
    fetchHotPosts();
  }, []);

  const fetchUserRooms = async () => {
    if (!user) return;
    setRoomsLoading(true);
    try {
      const response = await roomApi.getRooms();
      if (response.success && response.data) {
        if (user.role === '站长') {
          setUserRooms(response.data);
        } else {
          setUserRooms(response.data.filter((room: Room) => room.owner_id === user.id));
        }
      }
    } catch (error) {
      console.error('获取用户房间失败:', error);
    } finally {
      setRoomsLoading(false);
    }
  };

  const fetchCopyright = async () => {
    try {
      const response = await siteConfigApi.getConfig('site_copyright');
      if (response.success && response.data?.config_value) {
        setCopyrightText(response.data.config_value);
      }
    } catch (error) {
      console.error('获取版权信息失败:', error);
    }
  };

  const fetchSiteName = async () => {
    try {
      const response = await siteConfigApi.getConfig('site_name');
      if (response.success && response.data?.config_value) {
        setSiteName(response.data.config_value);
      }
    } catch (error) {
      console.error('获取站点名称失败:', error);
    }
  };

  const fetchGlobalAds = async () => {
    try {
      setAdsLoading(true);
      const response = await adApi.getActiveAds();
      if (response.success && response.data) {
        const globalAdList: Ad[] = [];
        if (response.data.global) {
          globalAdList.push(...response.data.global);
        }
        setGlobalAds(globalAdList);
        
        if (response.data.header_banner) {
          setHeaderAds(response.data.header_banner);
        }
      }
    } catch (error) {
      console.error('获取全局广告失败:', error);
    } finally {
      setAdsLoading(false);
    }
  };

  React.useEffect(() => {
    globalAds.forEach((ad) => {
      if (ad.ad_type === 'code' && ad.content && !injectedGlobalAds.has(ad.id)) {
        injectedGlobalAds.add(ad.id);
        
        const wrapper = document.createElement('div');
        wrapper.id = `ad-wrapper-${ad.id}`;
        wrapper.innerHTML = ad.content;
        document.body.appendChild(wrapper);
      }
    });
  }, [globalAds]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?keyword=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  const isAdmin = ['管理员', '站长', '超级管理员', '吧主', '吧务'].includes(user?.role || '');
  const isMaster = user?.role === '站长';
  const isRoomOwnerRole = user?.role === '房主' || user?.user_group_name === '房主';
  const isRoomOwner = isRoomOwnerRole || userRooms.length > 0;

  // 权限互斥：房主和管理员不能同时显示
  // 如果用户属于房主用户组，优先显示房间管理（房主就只是房主）
  const showRoomManagement = isRoomOwnerRole || (isRoomOwner && !isAdmin);
  const showAdminPanel = isAdmin && !isRoomOwnerRole;

  return (
    <div className="min-h-screen flex flex-col">
      {/* 顶部导航栏 */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img src="/favicon.svg" alt={siteName} className="w-8 h-8" />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-900">{siteName}</span>
                <span className="text-xs text-gray-500 -mt-1">交易者的世外桃源</span>
              </div>
            </Link>

            {/* 搜索框 - 桌面端 */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('common.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </form>

            {/* 右侧菜单 */}
            <div className="flex items-center space-x-4">
              <Link
                to="/rooms"
                className="hidden md:flex items-center text-gray-700 hover:text-primary-600 px-3 py-2 rounded-lg hover:bg-gray-100"
              >
                <MessageSquare className="w-4 h-4 mr-1.5 text-purple-500" />
                {t('nav.rooms')}
              </Link>

              {/* 语言切换 */}
              

              {isAuthenticated ? (
                <>
                  {/* 发帖按钮 */}
                  <Link
                    to="/create-post"
                    className="hidden sm:inline-flex btn btn-primary"
                  >
                    发帖
                  </Link>

                  {/* 通知 */}
                  <NotificationDropdown />

                  {/* 用户菜单 */}
                  <div className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <img
                        src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.nickname || user?.username || 'User'}&background=3b82f6&color=fff`}
                        alt={user?.nickname || user?.username}
                        className="w-8 h-8 rounded-full"
                      />
                      <span className="hidden sm:inline text-sm font-medium text-gray-700">
                        {user?.nickname}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    </button>

                    {/* 下拉菜单 */}
                    {userMenuOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setUserMenuOpen(false)}
                        />
                        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20">
                          <Link
                            to="/user-center"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <User className="w-4 h-4 mr-2" />
                            {t('nav.userCenter')}
                          </Link>
                          {showRoomManagement && (
                            <Link
                              to="/room-management"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <MessageSquare className="w-4 h-4 mr-2 text-purple-500" />
                              {t('nav.roomManagement')}
                            </Link>
                          )}
                          {showAdminPanel && (
                            <>
                              <div className="border-t border-gray-100 my-1"></div>
                              <Link
                                to="/admin"
                                onClick={() => setUserMenuOpen(false)}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <LayoutDashboard className="w-4 h-4 mr-2" />
                                {t('nav.admin')}
                              </Link>
                              {isMaster && (
                                <Link
                                  to="/admin-master"
                                  onClick={() => setUserMenuOpen(false)}
                                  className="flex items-center px-4 py-2 text-sm text-pink-600 hover:bg-pink-50"
                                >
                                  <Settings className="w-4 h-4 mr-2" />
                                  {t('nav.master')}
                                </Link>
                              )}
                            </>
                          )}
                          <div className="border-t border-gray-100 my-1"></div>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            {t('nav.logout')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link
                    to="/login"
                    className="btn btn-secondary"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-primary"
                  >
                    {t('nav.register')}
                  </Link>
                </div>
              )}

              {/* 移动端菜单按钮 */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* 移动端搜索框 */}
          <form onSubmit={handleSearch} className="md:hidden pb-3">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('common.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </form>
        </div>

        {/* 移动端菜单 */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-2">
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center px-3 py-2 rounded-lg hover:bg-gray-100"
                  >
                    <Home className="w-5 h-5 mr-2" />
                    {t('nav.home')}
                  </Link>
                  <Link
                    to="/chat"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center px-3 py-2 rounded-lg hover:bg-gray-100"
                  >
                    {t('nav.chat')}
                  </Link>
                  <Link
                    to="/rooms"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center px-3 py-2 rounded-lg hover:bg-gray-100"
                  >
                    {t('nav.rooms')}
                  </Link>
                  {isAuthenticated && (
                    <Link
                      to="/create-post"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center px-3 py-2 rounded-lg hover:bg-gray-100"
                    >
                      {t('nav.createPost')}
                    </Link>
                  )}
                  <div className="border-t border-gray-200 my-2"></div>
                  <h4 className="font-semibold text-gray-900 px-3 py-2">{t('nav.categories')}</h4>
                  {categories.slice(0, 6).map((category) => (
                    <div key={category.id}>
                      {category.children && category.children.length > 0 ? (
                        <div>
                          <button
                            onClick={() => setExpandedCategories(prev => 
                              prev.includes(category.id) 
                                ? prev.filter(id => id !== category.id)
                                : [...prev, category.id]
                            )}
                            className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <span>{category.display_name || category.name}</span>
                            <span className={`text-xs ${expandedCategories.includes(category.id) ? 'rotate-90' : ''}`}>▶</span>
                          </button>
                          {expandedCategories.includes(category.id) && (
                            <div className="ml-4 mt-1 space-y-1">
                              <Link
                                to={`/category/${category.id}`}
                                onClick={() => setMobileMenuOpen(false)}
                                className="block px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                全部
                              </Link>
                              {category.children.slice(0, 4).map((child) => (
                                <Link
                                  key={child.id}
                                  to={`/category/${child.id}`}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="block px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  {child.display_name || child.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={`/category/${category.id}`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          {category.display_name || category.name}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
          </div>
        )}

        {/* 顶部横幅广告位 */}
        {!adsLoading && headerAds.length > 0 && (
          <div className="border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <AdDisplay ads={headerAds} />
            </div>
          </div>
        )}
      </header>

      {/* 主内容区 */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex gap-6">
            {/* 左侧边栏 - 版块列表 */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="card p-4 sticky top-24">
                <h3 className="font-semibold text-gray-900 mb-3">导航</h3>
                <nav className="space-y-1">
                  <Link
                    to="/chat"
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    即时闲聊
                  </Link>
                  <Link
                    to="/rooms"
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    会员房间
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <h4 className="font-semibold text-gray-900 mb-2">版块</h4>
                  {categories.map((category) => (
                    <div key={category.id}>
                      {category.children && category.children.length > 0 ? (
                        <div>
                          <button
                            onClick={() => setExpandedCategories(prev => 
                              prev.includes(category.id) 
                                ? prev.filter(id => id !== category.id)
                                : [...prev, category.id]
                            )}
                            className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <span className="flex items-center">
                              {category.display_name || category.name}
                              <span className="ml-2 text-xs text-gray-400">
                                {category.total_posts || category.posts_count || 0}
                              </span>
                            </span>
                            <span className={`transform transition-transform ${expandedCategories.includes(category.id) ? 'rotate-90' : ''}`}>
                              ▶
                            </span>
                          </button>
                          {expandedCategories.includes(category.id) && (
                            <div className="ml-4 mt-1 space-y-1">
                              <Link
                                to={`/category/${category.id}`}
                                className="block px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                全部
                              </Link>
                              {category.children.map((child) => (
                                <Link
                                  key={child.id}
                                  to={`/category/${child.id}`}
                                  className="block px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  {child.display_name || child.name}
                                  <span className="ml-2 text-xs text-gray-400">
                                    {child.posts_count || 0}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={`/category/${category.id}`}
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          {category.display_name || category.name}
                          <span className="ml-2 text-xs text-gray-400">
                            {category.total_posts || category.posts_count || 0}
                          </span>
                        </Link>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
            </aside>

            {/* 主内容 */}
            <div className="flex-1 min-w-0">
              <Outlet />
            </div>

            {/* 右侧边栏 */}
            <aside className="hidden xl:block w-72 flex-shrink-0">
              <div className="card p-4 sticky top-24">
                <h3 className="font-semibold text-gray-900 mb-3">热门帖子</h3>
                <div className="space-y-3">
                  {hotPostsLoading ? (
                    <p className="text-sm text-gray-500">加载中...</p>
                  ) : hotPosts.length > 0 ? (
                    hotPosts.map((post, index) => (
                      <Link
                        key={post.id}
                        to={`/post/${post.id}`}
                        className="block p-2 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <span className={`text-xs font-medium w-5 h-5 flex items-center justify-center rounded ${
                            index === 0 ? 'bg-red-500 text-white' :
                            index === 1 ? 'bg-orange-500 text-white' :
                            index === 2 ? 'bg-yellow-500 text-white' :
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 truncate">{post.title}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {post.replies_count}评论 · {post.likes}点赞
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">暂无数据</p>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              {copyrightText}
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/about-us" className="text-sm text-gray-500 hover:text-gray-700">
                关于我们
              </Link>
              <Link to="/join-us" className="text-sm text-gray-500 hover:text-gray-700">
                加入我们
              </Link>
              <Link to="/business-cooperation" className="text-sm text-gray-500 hover:text-gray-700">
                商务合作
              </Link>
              <Link to="/donation" className="text-sm text-gray-500 hover:text-gray-700">
                支持我们
              </Link>
              <Link to="/terms-of-service" className="text-sm text-gray-500 hover:text-gray-700">
                服务协议
              </Link>
              <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-gray-700">
                隐私政策
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* 通知横幅 */}
      <NotificationBanner />
    </div>
  );
};

export default Layout;
